/**
 * Postgres rows → the field names the application actually reads.
 *
 * ===========================================================================
 * WHY THIS FILE EXISTS
 * ===========================================================================
 *
 * `org_bootstrap` returns `to_jsonb(a) FROM public.accounts`, so a row arrives
 * as `account_name` / `account_number` / `acct_type`. The application reads
 * `accountName` / `accountNumber` / `accountType` — in 161, 129 and 110 files
 * respectively, and `fundName` / `fundNumber` in 113 and 63 more. Without a
 * translation between the two, the Postgres path does not render "wrong data";
 * it renders nothing:
 *
 *     InlineAccountForm.jsx:395   account.accountType.toUpperCase()
 *       → TypeError: Cannot read properties of undefined (reading 'toUpperCase')
 *     FundTransferDialog.jsx:321  a.fundName.localeCompare(b.fundName)
 *       → TypeError: Cannot read properties of undefined (reading 'localeCompare')
 *
 * Both components are mounted inside collapsed sections on their pages, so the
 * throw is unconditional on load and the error boundary swallows the whole
 * screen. Measured 2026-09-05: with the flag on, /foundation/chart-of-accounts
 * and /foundation/funds-list both rendered zero rows.
 *
 * WHY NEITHER PARITY GATE CAUGHT IT. refdata-parity.sh compares COUNTS and
 * hierarchy-parity.mjs compares IDS — and counts and ids are precisely what
 * survives a field-name mismatch untouched. Every slice agreed perfectly while
 * every screen was broken. That is the argument for the browser test, and the
 * reason this module is written against the transformer rather than against
 * either gate.
 *
 * ===========================================================================
 * WHY HERE AND NOT IN THE RPC
 * ===========================================================================
 *
 * Renaming these columns inside org_bootstrap would fix the same screens, and
 * would be wrong: it bakes a Firestore-era vocabulary into the database API of
 * a migration whose endpoint is a Postgres-native app. src/data/ is the
 * compatibility seam by design — hierarchy.js already rebuilds three documents
 * here for the same reason — so the vocabulary translation lives at the seam
 * and dies with it. The database keeps its own names.
 *
 * ===========================================================================
 * THE MAPPING IS THE TRANSFORMER'S INVERSE
 * ===========================================================================
 *
 * Every line below is the reverse of an assignment in migration/src/map/. That
 * is what makes it checkable rather than guessed:
 *
 *   accounts.js:219-239   parent_account_id, acct_type, account_name,
 *                         account_number, is_register, illiquid,
 *                         is_payable_receivable, disabled,
 *                         dismiss_starting_balance, donor_reason,
 *                         is_restricted, end_date, merged_into, merged_at
 *   labels.js:80-87       parent_label_id, label_name, label_number, disabled
 *
 * Funds arrive already half-renamed: `v_funds` projects `account_name` as
 * `fund_name` and `parent_account_id` as `parent_fund_id`, because funds and
 * accounts are one table (the F-merge). Only the snake-to-camel step remains.
 *
 * WHAT IS DELIBERATELY NOT EMITTED. Columns with no Firestore counterpart —
 * org_id, group_id, sort_order, created_at, updated_at, last_changed_by, and
 * the generated depth/parent_depth — are dropped rather than passed through.
 * Placement is carried by the three hierarchy documents, not by fields on the
 * row, and inventing keys the documents never had is how a second vocabulary
 * starts. Firestore's own write-path metadata (migrationBatchId, skipHistory,
 * userMadeChange, createdAt) is likewise absent: nothing reads it for display,
 * and it does not exist on this side.
 */

/** Copy `from[key]` to `out[name]` only when the column is present. */
const put = (out, from, key, name) => {
  if (from[key] !== undefined) out[name] = from[key];
  return out;
};

/**
 * One `public.accounts` row → an account document.
 *
 * The four fund-only columns (donor_reason, is_restricted, end_date, and the
 * merge pair) are null on ordinary accounts, exactly where Firestore omitted
 * them — null and absent are both falsy, so every existing guard behaves the
 * same. They are carried because a merged row IS a fund and the fund screens
 * read them.
 */
export function toAccount(row) {
  const out = { id: row.id };
  put(out, row, "account_name", "accountName");
  put(out, row, "account_number", "accountNumber");
  put(out, row, "acct_type", "accountType");
  put(out, row, "is_register", "isRegister");
  put(out, row, "illiquid", "illiquid");
  put(out, row, "is_payable_receivable", "isPayableReceivable");
  put(out, row, "disabled", "disabled");
  put(out, row, "dismiss_starting_balance", "dismissStartingBalance");
  put(out, row, "donor_reason", "donorReason");
  put(out, row, "is_restricted", "isRestricted");
  put(out, row, "end_date", "endDate");
  put(out, row, "merged_into", "mergedInto");
  put(out, row, "merged_at", "mergedAt");
  return out;
}

/**
 * One `public.v_funds` row → a fund document.
 *
 * isUsableFund() (src/utilities/general_util.jsx:153) reads exactly disabled,
 * fundName and fundNumber, and drives `activeFunds`, `isSingleFundOrg` and
 * `defaultFund` in the provider. Get any of those three wrong and a
 * single-fund org silently stops auto-selecting its fund on every entry form.
 */
export function toFund(row) {
  const out = { id: row.id };
  put(out, row, "fund_name", "fundName");
  put(out, row, "fund_number", "fundNumber");
  put(out, row, "disabled", "disabled");
  put(out, row, "donor_reason", "donorReason");
  put(out, row, "is_restricted", "isRestricted");
  put(out, row, "end_date", "endDate");
  put(out, row, "merged_into", "mergedInto");
  put(out, row, "merged_at", "mergedAt");
  return out;
}

/**
 * jan…dec, the order every fiscal-year consumer indexes by.
 *
 * Fiscal-year start crosses a TYPE boundary, not just a name one: Firestore held
 * a lowercase three-letter month token, the schema holds smallint 1-12. All 43
 * consumers parse the token — MONTH_MAP in dateRangePresets.js, MONTH_INDEX in
 * budgetBulletData.js, calculateYearToDate in IncomeStatement.jsx — so the token
 * is what has to come back out.
 *
 * Handing them the integer would not throw. `MONTH_MAP[3]` is undefined,
 * fyStartMonth() falls back to 0, and every report in the app would quietly
 * re-base onto a January fiscal year. A March-start org would see wrong numbers,
 * not an error, which is why this is a conversion and not a rename.
 *
 * Out-of-range resolves to "jan", and it is the SAME fallback both sides already
 * make — the consumers apply it to an unknown token,
 * `account_activity_between` coalesces the column to 1 (`0143:101-105`), and
 * `handleCalcReport` coalesced the Firestore field to "jan"
 * (`unifiedReports.js:612`) — so an org with no value set buckets identically on
 * every path rather than one of them inventing a different year.
 *
 * ONE DEFINITION, because the conversion decides which fiscal year every report
 * buckets into (plan O4, "a huge deal"). It was written twice — once for the
 * browser's `org_bootstrap` payload, once for the report Function's five-table
 * read — and two copies of a table indexed by an off-by-one is two chances to
 * get the off-by-one wrong.
 */
export const FY_MONTH_TOKENS = Object.freeze([
  "jan", "feb", "mar", "apr", "may", "jun",
  "jul", "aug", "sep", "oct", "nov", "dec",
]);

export const fiscalYearToken = (month) => FY_MONTH_TOKENS[Number(month) - 1] ?? "jan";

/**
 * One `public.orgs` row → the fields BOTH org readers derive from it.
 *
 * The browser rebuilds the whole `orgs/{orgId}` document on top of this
 * (`useOrgRefdata.mapOrgDocument`: members, hierarchy, trial, the effective
 * feature map); the report Function reads the row for its fiscal calendar and
 * its feature overrides. What they SHARE is these two derivations, and sharing
 * them is the point — a report that bucketed by a different fiscal year than the
 * screen that requested it is plan O4's silent misbucketing.
 *
 * `features` is left exactly as the column holds it: `orgs.features` is the
 * per-org OVERRIDE namespace (schema §S8), NOT the effective entitlement map,
 * and `featureOverrides` names it so a reader cannot mistake one for the other.
 * The browser layers `v_org_effective_features` over the `features` key
 * afterwards; a caller with no effective map (the Function) must not pretend to
 * have one.
 */
export function toOrg(row) {
  if (!row) return null;
  return {
    ...row,
    financialYearStart: fiscalYearToken(row.fiscal_year_start_month),
    featureOverrides: row.features ?? {},
  };
}

/** One `public.labels` row → a label document. */
export function toLabel(row) {
  const out = { id: row.id };
  put(out, row, "label_name", "labelName");
  put(out, row, "label_number", "labelNumber");
  put(out, row, "disabled", "disabled");
  return out;
}

/**
 * THE FLAT ARRAYS COME OUT IN DOCUMENT-ID ORDER, because that is what the
 * listeners they replace deliver.
 *
 * `collection(db, "orgs/X/accounts")` carries no orderBy, so Firestore returns
 * documents ordered by `__name__` — the id. `org_bootstrap` orders accounts by
 * `account_number, id` and funds by `sort_order, id`. Both are defensible
 * orderings; they are not the SAME ordering, and the difference is not cosmetic
 * because two live consumers index position 0:
 *
 *   useReceivePaymentForm.js:105  funds[0]?.id — the fund an overpayment on a
 *     customer receipt is parked in. A different first fund posts real money to
 *     a different fund, with nothing on screen to say so.
 *   account_selector.jsx:23,35    accounts[0] — the register that opens by default.
 *
 * So flipping VITE_USE_POSTGRES_REFDATA has to be invisible here, and the only
 * order that makes it invisible is the listeners'. Sorting client-side rather
 * than in the RPC keeps the database's own API sensibly ordered (by account
 * number, by sort order) for everything that is not impersonating a Firestore
 * listener — and this sort dies with the seam.
 *
 * A code-unit compare, not localeCompare: Firestore orders `__name__` by UTF-8
 * bytes, which for these ids is the same thing, while localeCompare would answer
 * differently under a `tr` or `sv` browser locale (and this repo has been bitten
 * by locale-dependent collation once already — see fix/schema-collation-order).
 *
 * The hierarchies are NOT sorted here: they carry their own order, built from
 * sort_order on the raw rows (src/data/hierarchy.js).
 */
const byId = (a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);

export const toAccounts = (rows = []) => rows.map(toAccount).sort(byId);
export const toFunds = (rows = []) => rows.map(toFund).sort(byId);
export const toLabels = (rows = []) => rows.map(toLabel).sort(byId);

/**
 * Raw rows → the six-field chart the app and the tree builder both read.
 *
 * The six lines below had been written twice — once in `useOrgRefdata.mapSnapshot`
 * for the browser's `org_bootstrap` payload, once in
 * `src/reports/server/refdata.js` for the Function's five-table read — from two
 * different row sources that produce the same shape. Both had to get the same
 * two non-obvious things right, and a second copy is a second chance to get one
 * of them wrong:
 *
 *   1. The FLAT arrays are translated and sorted by id; the HIERARCHIES are
 *      built from the RAW rows and must NOT be re-sorted. They carry their own
 *      order, from `sort_order` on the Postgres column names — translating first
 *      would break their ordering while changing nothing they output.
 *   2. `funds` feeds `buildHierarchies` as well as its own array, because the
 *      chart's Equity section is rebuilt from fund placement (src/data/hierarchy.js).
 *
 * PARTIAL BY DESIGN. A caller that fetched only some of the slices passes only
 * those, and gets back only the keys it can have — which is what lets a report
 * that needs one account off the chart read one table instead of five. A key is
 * present when its inputs are; it is never present-and-empty, because an empty
 * `accounts` array does not throw anywhere downstream, it renders an empty
 * report (`refdata.js`'s D3 note).
 *
 * @param {{accounts?: object[], funds?: object[], labels?: object[],
 *          accountGroups?: object[], labelGroups?: object[]}} rows RAW rows.
 * @param {{buildHierarchies: Function, buildLabelsHierarchy: Function}} builders
 *   From `src/data/hierarchy.js`. Injected rather than imported so this module
 *   stays import-free — the property that lets the Vercel Function and the
 *   browser bundle hold the same copy of it.
 */
export function shapeChart({ accounts, funds, labels, accountGroups, labelGroups }, builders) {
  const chart = {};
  if (accounts) chart.accounts = toAccounts(accounts);
  if (funds) chart.funds = toFunds(funds);
  if (labels) chart.labels = toLabels(labels);

  if (accountGroups && accounts && funds) {
    const { accountsHierarchy, fundsHierarchy } = builders.buildHierarchies(
      accountGroups,
      accounts,
      funds,
    );
    chart.accountsHierarchy = accountsHierarchy;
    chart.fundsHierarchy = fundsHierarchy;
  }
  if (labelGroups && labels) {
    chart.labelsHierarchy = builders.buildLabelsHierarchy(labelGroups, labels);
  }
  return chart;
}

/* ===========================================================================
 * LEDGER ENTRIES — `ledger_entries_page` rows → the transaction documents the
 * register, the Journal Entries screen and the reconciliation tool read.
 *
 * Contract: docs/design/txn-list-pg-contract.md §B (SQL side) and §F (this
 * side). Same rule as the chart above: the database keeps its own names, the
 * screens keep the Firestore vocabulary, and the translation lives here so it
 * dies with the seam. Every field below was verified against a consumer that
 * reads it by name (the contract's §F lists them); a field with no reader is
 * not emitted.
 * =========================================================================== */

/**
 * THE ONE calendar-day reduction. A Date, a dayjs, a Firestore Timestamp or a
 * date-ish string → 'YYYY-MM-DD', or null when the value is unusable.
 *
 * `local` is the whole reason there is an option rather than two functions.
 * The DEFAULT is the UTC day, which is what a §F row's `date` (a Date at UTC
 * noon) and a bare 'YYYY-MM-DD' both already are — the predicate compares
 * days as strings on that basis (`ledgerPredicate.toYmd`). The register hands
 * its hook `endDate` at LOCAL 23:59:59.999, whose UTC day in any western zone
 * is TOMORROW — one day too many on an inclusive upper bound — so the option
 * translation renders those from the Date's LOCAL fields
 * (`ledgerAdapters.localYmd`). Both readings existed as separate copies of the
 * same parser; this is the copy they now share.
 *
 * dayjs is only consulted through `.format` under `local`, because that is
 * what the local reading wants and `.toDate()` (which dayjs also has) is the
 * UTC reading the default already gives.
 */
export function ymd(value, { local = false } = {}) {
  if (value == null || value === "") return null;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    if (!local) return value.toISOString().slice(0, 10);
    const mo = String(value.getMonth() + 1).padStart(2, "0");
    const d = String(value.getDate()).padStart(2, "0");
    return `${value.getFullYear()}-${mo}-${d}`;
  }
  if (typeof value === "string") {
    const m = /^(\d{4}-\d{2}-\d{2})/.exec(value);
    if (m) return m[1];
    return ymd(new Date(value), { local });
  }
  if (local && typeof value.format === "function") return value.format("YYYY-MM-DD"); // dayjs
  if (typeof value.toDate === "function") return ymd(value.toDate(), { local }); // Timestamp
  return null;
}

/**
 * 'YYYY-MM-DD' → a Date at UTC noon, which is createFinancialDate's convention
 * (src/utilities/dateUtils.js:96-140): noon keeps the calendar day stable
 * under every consumer's local-time formatting. Written here rather than
 * imported because this module is import-free on purpose (see the header).
 * A Date or a timestamp string is passed through the same normalization so
 * the optimistic path and the server path agree to the day.
 */
export function financialDate(value) {
  const day = ymd(value);
  if (day === null) return null;
  const [y, m, d] = day.split("-");
  return new Date(Date.UTC(Number(y), Number(m) - 1, Number(d), 12));
}

const toDate = (v) => {
  if (v == null || v === "") return null;
  const d = v instanceof Date ? v : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** kind → the boolean flags the screens switch on (contract §F). */
const KIND_FLAGS = Object.freeze({
  starting_balance: { isStartingBalance: true },
  donation: { isDonation: true },
  donation_sweep: { isDonationSweep: true, isDonationBatch: true },
  plaid: { fromPlaid: true },
});

/**
 * `managedBy` provenance markers, written as literals rather than as exported
 * constants: this module is import-free (see the header), so it cannot import
 * the ONE definition of the AP/AR marker (`MANAGED_BY_AP_AR`,
 * src/locations/apar/aparDerivations.js:35), and a second exported constant is
 * how a third one starts. Consumers that need the name import it from there.
 */
const MANAGED_BY = Object.freeze({ linked: "linked-account", apAr: "ap-ar" });

/**
 * One element of the `lines` jsonb → one app-shaped line.
 *
 * `cleared` is derived from the entry's `cleared_account_ids`, so every line
 * carries its own flag for every account (contract §F.1 item 4): the inspect
 * dialog (inspect_transaction.jsx:170) and the JE screen's cleared icon
 * (transactions.js:1113) read `line.cleared` org-wide, not only on the viewed
 * account. Nullable dimensions are OMITTED, not nulled — `line.label`
 * truthiness is how transactions.js:379 decides whether to look a label up.
 */
export function toLine(pgLine, clearedAccountIds = []) {
  const out = {
    lineNo: pgLine.line_no,
    account: pgLine.account_id,
    fund: pgLine.fund_id,
    amount: Number(pgLine.amount_cents),
    sign: pgLine.side,
    signedCents: Number(pgLine.signed_cents),
    cleared: Array.isArray(clearedAccountIds) && clearedAccountIds.includes(pgLine.account_id),
  };
  if (pgLine.label_id != null) out.label = pgLine.label_id;
  if (pgLine.contact_id != null) out.contact = pgLine.contact_id;
  if (pgLine.donor_id != null) out.donor = pgLine.donor_id;
  if (pgLine.comment != null) out.comment = pgLine.comment;
  if (pgLine.party_kind != null) out.partyKind = pgLine.party_kind;
  if (pgLine.party_name != null) out.partyName = pgLine.party_name;
  return out;
}

/** Deduped, in order of first appearance — what `filterTransactions` and the exporter index. */
const distinct = (lines, key) => {
  const seen = new Set();
  const out = [];
  for (const l of lines) {
    const v = l[key];
    if (v != null && v !== "" && !seen.has(v)) { seen.add(v); out.push(v); }
  }
  return out;
};

/** The four dimension arrays every Firestore entry document carried. */
export function deriveEntryArrays(lines = []) {
  return {
    accounts: distinct(lines, "account"),
    funds: distinct(lines, "fund"),
    contacts: distinct(lines, "contact"),
    labels: distinct(lines, "label"),
  };
}

/**
 * One `ledger_entries_page` row → one transaction document (contract §F).
 *
 * `links` keys arrive with nulls stripped; a missing key and a null key both
 * leave the app field absent, which is how Firestore omitted them — every
 * consumer guards with `?.` or truthiness (register_transactions_new.jsx:456-514).
 */
export function toEntryRow(row) {
  const clearedAccountIds = Array.isArray(row.cleared_account_ids) ? row.cleared_account_ids : [];
  const lines = Array.isArray(row.lines) ? row.lines.map((l) => toLine(l, clearedAccountIds)) : [];
  const links = row.links && typeof row.links === "object" ? row.links : {};

  const out = {
    id: row.entry_id,
    kind: row.kind,
    versionId: row.version_id,
    versionNo: row.version_no,
    date: financialDate(row.entry_date),
    createdAt: toDate(row.created_at),
    postedAt: toDate(row.posted_at),
    lines,
    ...deriveEntryArrays(lines),
    cleared: row.cleared ?? null,
    clearedAccountIds,
    receiptCount: Number(row.receipt_count ?? 0),
    ...(KIND_FLAGS[row.kind] ?? {}),
  };
  if (row.entry_number != null) {
    out.entryNumber = row.entry_number;
    out.journalEntry = row.entry_number;
  }
  put(out, row, "author_user_id", "authorUserId");
  if (row.memo != null) out.memo = row.memo;
  if (row.description != null) out.description = row.description;
  if (row.check_number != null) out.checkNumber = row.check_number;
  if (row.cleared_by_reconciliation_id != null) out.clearedByReconciliationId = row.cleared_by_reconciliation_id;
  if (row.marked_in_reconciliation_id != null) out.markedInReconciliationId = row.marked_in_reconciliation_id;
  if (row.opening_cents != null) out.openingCents = Number(row.opening_cents);
  if (row.running_cents != null) out.runningCents = Number(row.running_cents);

  if (links.ap_ar_document_id != null) out.apArDocumentId = links.ap_ar_document_id;
  if (links.donation_id != null) out.donationId = links.donation_id;
  if (links.batch_id != null) out.batchId = links.batch_id;
  if (links.transfer_amount_cents != null) out.transferAmountCents = Number(links.transfer_amount_cents);
  if (links.bill_com_disputed != null) out.billComDisputed = Boolean(links.bill_com_disputed);
  if (links.source_card_id != null) out.sourceCardId = links.source_card_id;

  if (row.kind === "linked_transfer") out.managedBy = MANAGED_BY.linked;
  else if (links.ap_ar_document_id != null) out.managedBy = MANAGED_BY.apAr;
  return out;
}

export const toEntryRows = (rows = []) => rows.map(toEntryRow);

/** The placeholder the JE form stores in a line's contact field. Not an id. */
const CONTACT_PLACEHOLDER = "Journal Entry";

/**
 * THE ONE coercion a form line goes through, whichever direction it is headed.
 *
 * The optimistic row and the RPC payload (`toRpcLine`, src/data/ledger.js) are
 * the same rules under two vocabularies: the `x_id ?? x` cascade, the dropped
 * contact placeholder, and — the one that is not obvious — a NEGATIVE amount
 * is a legacy value, not a credit, so the side flips and the magnitude stays
 * (`amount_cents` CHECKs > 0 and would refuse it either way). Written twice,
 * the optimistic row and the row the server sends back could disagree on the
 * side of a legacy line, which is exactly the kind of divergence nobody sees
 * until a user's own entry flips when it refreshes.
 *
 * Nothing here throws: `toRpcLine` is the validating caller (it is the one
 * addressing a NOT NULL column) and it reads `rawAmount` / `rawSide` for that.
 *
 * @returns {{rawAmount:number, rawSide:*, finite:boolean, amount:number,
 *            side:*, account:*, fund:*, label?:*, contact?:*, donor?:*, comment?:*}}
 */
export function coerceFormLine(line) {
  const rawAmount = Number(line.amount ?? line.amount_cents ?? 0);
  const rawSide = line.side ?? line.sign;
  const finite = Number.isFinite(rawAmount);
  const negative = finite && rawAmount < 0;
  const out = {
    rawAmount,
    rawSide,
    finite,
    amount: finite ? Math.abs(Math.round(rawAmount)) : 0,
    side: negative ? (rawSide === "debit" ? "credit" : "debit") : rawSide,
    account: line.account_id ?? line.account,
    fund: line.fund_id ?? line.fund,
  };
  const label = line.label_id ?? line.label;
  const contact = line.contact_id ?? line.contact;
  const donor = line.donor_id ?? line.donor;
  if (label) out.label = label;
  if (contact && contact !== CONTACT_PLACEHOLDER) out.contact = contact;
  if (donor) out.donor = donor;
  if (line.comment) out.comment = line.comment;
  return out;
}

/** A form line → an app-shaped line: `coerceFormLine` under §F's names. */
export function normalizeFormLine(line, lineNo) {
  const c = coerceFormLine(line);
  const out = {
    lineNo,
    account: c.account,
    fund: c.fund,
    amount: c.amount,
    sign: c.side,
    signedCents: c.side === "debit" ? c.amount : -c.amount,
    cleared: Boolean(line.cleared),
  };
  if (c.label) out.label = c.label;
  if (c.contact) out.contact = c.contact;
  if (c.donor) out.donor = c.donor;
  if (c.comment) out.comment = c.comment;
  return out;
}

/**
 * The optimistic row (contract §F.3). Same vocabulary as toEntryRow's output,
 * so the store can upsert it by id and the server row replaces it field for
 * field. `versionId` is left undefined on purpose — only the server knows it,
 * and `refresh([id])` fills it in.
 */
export function optimisticEntryRow({ id, date, createdAt, memo, description, checkNumber, lines = [], kind = "manual" }) {
  const normalized = lines.map((l, i) => normalizeFormLine(l, i + 1));
  const out = {
    id,
    kind,
    date: financialDate(date),
    createdAt: toDate(createdAt) ?? new Date(),
    lines: normalized,
    ...deriveEntryArrays(normalized),
    cleared: null,
    clearedAccountIds: [],
    receiptCount: 0,
    optimistic: true,
    ...(KIND_FLAGS[kind] ?? {}),
  };
  if (memo != null) out.memo = memo;
  if (description != null) out.description = description;
  if (checkNumber != null && checkNumber !== "") out.checkNumber = checkNumber;
  return out;
}
